import { Show } from "components/core";
import { ExternalLink, Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import useApi from "utils/use-api";
import PlacingSlipFilePicker from "./placing-slip-file-picker";
import PlacingSlipPreviewCard from "./placing-slip-preview-card";
import { useExistingPlacingSlipPreviewUrl } from "./placing-slip-file-preview";
import PlacingSlipUploadStatus from "./placing-slip-upload-status";
import {
    checkPlacingSlipExists,
    deleteUnsignedPlacingSlip,
    openPlacingSlipView,
    uploadUnsignedPlacingSlip,
} from "./placing-slip-api";
import StepFooter from "./step-footer";

type Props = {
    handleNext: (_values: any) => void;
    handlePrevious: () => void;
    handleCancel: () => void;
    initialValues: any;
    offerId?: string;
    parentForm?: any;
};

const PlacingSlipEditForm = ({
    handleCancel,
    handleNext,
    initialValues,
    handlePrevious,
    offerId,
    parentForm,
}: Props) => {
    const api = useApi();
    const [fetching, setFetching] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [viewing, setViewing] = useState(false);

    const form = useFormik<any>({
        initialValues: {
            pendingFiles: [],
            hasExisting: initialValues?.hasExisting || false,
        },
        onSubmit: (values) => {
            syncParent(values);
            handleNext(values);
        },
        onReset: () => {
            handleCancel();
        },
    });

    const syncParent = (values: any) => {
        parentForm?.setFieldValue("placingSlip", values);
    };

    const fetchExisting = async () => {
        if (!offerId) return;
        setFetching(true);
        try {
            const exists = await checkPlacingSlipExists(api, offerId);
            form.setFieldValue("hasExisting", exists);
            syncParent({
                pendingFiles: form.values.pendingFiles,
                hasExisting: exists,
            });
        } catch {
            toast.error("Could not load placing slip");
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchExisting();
    }, [offerId]);

    useEffect(() => {
        form.setValues({
            pendingFiles: initialValues?.pendingFiles || [],
            hasExisting: initialValues?.hasExisting ?? form.values.hasExisting,
        });
    }, [initialValues?.pendingFiles]);

    useEffect(() => {
        const nextBusy = fetching || uploading || deleting;
        if (parentForm?.values?.placingSlipBusy === nextBusy) {
            return;
        }
        parentForm?.setFieldValue("placingSlipBusy", nextBusy);
    }, [fetching, uploading, deleting]);

    const hasExistingPlacingSlip = form.values.hasExisting;
    const isBusy = fetching || uploading || deleting;
    const { previewUrl, loading: previewLoading } =
        useExistingPlacingSlipPreviewUrl(
            offerId || "",
            Boolean(hasExistingPlacingSlip && offerId),
        );

    const handleDelete = async () => {
        if (!offerId || isBusy) return;

        setDeleting(true);
        try {
            await deleteUnsignedPlacingSlip(api, offerId);
            toast.success("Placing slip deleted successfully");
            form.setFieldValue("hasExisting", false);
            form.setFieldValue("pendingFiles", []);
            syncParent({ pendingFiles: [], hasExisting: false });
        } catch {
            toast.error("Could not delete placing slip");
        } finally {
            setDeleting(false);
        }
    };

    const handleFileChange = async (files: File[]) => {
        if (isBusy) return;

        if (!files.length) {
            form.setFieldValue("pendingFiles", []);
            syncParent({
                pendingFiles: [],
                hasExisting: form.values.hasExisting,
            });
            return;
        }

        if (!offerId) {
            form.setFieldValue("pendingFiles", files);
            return;
        }

        setUploading(true);
        try {
            await uploadUnsignedPlacingSlip(api, offerId, files[0]);
            toast.success("Placing slip uploaded successfully");
            form.setFieldValue("hasExisting", true);
            form.setFieldValue("pendingFiles", []);
            syncParent({ pendingFiles: [], hasExisting: true });
        } catch {
            toast.error("Could not upload placing slip");
            form.setFieldValue("pendingFiles", []);
        } finally {
            setUploading(false);
        }
    };

    const handleView = async () => {
        if (!offerId || isBusy) return;

        setViewing(true);
        try {
            await openPlacingSlipView(api, offerId);
        } catch {
            toast.error("Could not open placing slip");
        } finally {
            setViewing(false);
        }
    };

    return (
        <div className="flex-1 flex flex-col overflow-hidden relative">
            <form
                onSubmit={form.handleSubmit}
                className="space-y-6 divide-y divide-gray-200 overflow-y-auto flex-1 relative">
                <div className="p-6 space-y-4 relative">
                    {uploading && (
                        <PlacingSlipUploadStatus variant="overlay" />
                    )}
                    <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            Upload Placing Slip
                        </h3>
                        <p className="mt-1 text-xs text-gray-500">
                            {hasExistingPlacingSlip
                                ? "View or delete the uploaded placing slip below."
                                : "Upload the placing slip document for this offer."}
                        </p>
                    </div>

                    <Show if={fetching}>
                        <PlacingSlipUploadStatus message="Loading existing placing slip..." />
                    </Show>

                    <Show if={!fetching && hasExistingPlacingSlip}>
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Placing Slip
                            </label>
                            <PlacingSlipPreviewCard
                                previewUrl={previewUrl}
                                previewLoading={previewLoading}
                                name="Placing Slip"
                                badge="Uploaded document"
                                actions={
                                    <div className="flex shrink-0 items-center gap-3">
                                        <button
                                            type="button"
                                            disabled={isBusy}
                                            onClick={handleView}
                                            className="text-primary-600 hover:underline flex items-center gap-1 text-xs disabled:opacity-50 disabled:cursor-not-allowed">
                                            <ExternalLink className="w-3.5 h-3.5" />
                                            {viewing ? "Opening..." : "View"}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={isBusy}
                                            onClick={handleDelete}
                                            className="text-red-600 disabled:cursor-not-allowed hover:underline flex items-center gap-1 text-xs disabled:opacity-50">
                                            <Show if={!deleting}>
                                                <>
                                                    <Trash className="w-3.5 h-3.5" />
                                                    Delete
                                                </>
                                            </Show>
                                            <Show if={deleting}>
                                                <span>Deleting...</span>
                                            </Show>
                                        </button>
                                    </div>
                                }
                            />
                        </div>
                    </Show>

                    <Show if={!fetching && !hasExistingPlacingSlip}>
                        <PlacingSlipFilePicker
                            files={form.values.pendingFiles}
                            onChange={handleFileChange}
                            disabled={isBusy}
                            uploading={uploading}
                        />
                    </Show>
                </div>
            </form>
            <StepFooter
                onNext={form.handleSubmit}
                onCancel={form.resetForm}
                onPrevious={handlePrevious}
                showPrevious={true}
                busy={isBusy}
                busyLabel={
                    uploading
                        ? "Uploading..."
                        : deleting
                          ? "Deleting..."
                          : "Loading..."
                }
            />
        </div>
    );
};

export default PlacingSlipEditForm;
